import type { ReactNode } from "react";
import { Filter, X } from "lucide-react";
import { BAND_DOT, KIND_ORDER, type ScanFacets, type ScanBand, type SiteKind } from "../../../../lib/siteScan";
import { Btn, FilterMenu, SearchField, type FilterMenuItem } from "../../../ui";
import type { T } from "../types";
import {
  BANDS,
  CONTACT_FLAGS,
  SITE_FLAGS,
  clearFlags,
  flagCount,
  scanFilterChips,
  selectedFlags,
  type ScanFlag,
} from "./filterGroups";
import { hasActiveFilters, type ScanFilterState } from "./useScansTable";

/**
 * The toolbar above the scans table.
 *
 * Every filter here answers one question an operator already has in their head
 * ("show me the WordPress ones that are behind and have an email address"), and
 * each one carries the count of what it would find, so nobody has to click a
 * filter to discover it is empty.
 *
 * It used to say all of that at once: nineteen toggles across three rows, plus
 * the search row, which is a third of the viewport spent on filters before
 * anyone has filtered anything - and the table itself, the thing the page is
 * for, pushed under the fold. The toggles are now four dropdowns on one line,
 * grouped the way the questions group, with the counts kept inside the menus.
 * What is actually set stays visible as removable chips underneath, which is
 * the shape the register page already uses: a collapsed filter has to say it is
 * collapsed onto something, or a narrowed list and an empty one read the same.
 */
export function ScanFilters({
  filters,
  patch,
  reset,
  qInput,
  setQInput,
  facets,
  total,
  actions,
  t,
}: {
  filters: ScanFilterState;
  patch: (patch: Partial<ScanFilterState>) => void;
  reset: () => void;
  qInput: string;
  setQInput: (v: string) => void;
  facets: ScanFacets | null;
  total: number;
  /** The table's own buttons (refresh, export), parked at the end of this row.
   *  They belong to the same list as the filters, and on their own line above
   *  they sat in the empty half of the heading row looking like a page action
   *  rather than a table one. */
  actions?: ReactNode;
  t: T;
}) {
  const toggleBand = (band: string) => {
    const current = filters.band ?? [];
    const next = band as ScanBand;
    patch({ band: current.includes(next) ? current.filter((b) => b !== next) : [...current, next] });
  };

  const toggleCms = (id: string) => {
    patch({ cms: filters.cms.includes(id) ? filters.cms.filter((c) => c !== id) : [...filters.cms, id] });
  };

  const toggleKind = (kind: string) => {
    const current = filters.kind ?? [];
    const next = kind as SiteKind;
    patch({ kind: current.includes(next) ? current.filter((k) => k !== next) : [...current, next] });
  };

  const toggleFlag = (flag: string) => patch({ [flag as ScanFlag]: !filters[flag as ScanFlag] });

  const flagItems = (group: readonly ScanFlag[]): FilterMenuItem[] =>
    group.map((flag) => ({
      value: flag,
      label: t(`scanner.table.filter.${flag}`),
      count: flagCount(flag, facets),
    }));

  const bandItems: FilterMenuItem[] = BANDS.map((band) => ({
    value: band,
    label: t(`scanner.score.${band}`),
    count: facets?.bands[band],
    dot: BAND_DOT[band],
  }));

  // Every kind is offered, in the fixed order, even at zero: the list is eight
  // items long and its shape is part of the answer. "Portfolio: 0" says the
  // scanner has not met one yet, which a hidden row would not.
  const kindCounts = new Map((facets?.kinds ?? []).map((k) => [k.id, k.count]));
  const kindItems: FilterMenuItem[] = KIND_ORDER.map((kind) => ({
    value: kind,
    label: t(`scanner.kind.${kind}`),
    count: facets ? (kindCounts.get(kind) ?? 0) : undefined,
  }));

  // Only platforms that actually turn up are offered, biggest first. A menu of
  // 200 fingerprints would be a worse filter than no filter.
  const platformItems: FilterMenuItem[] = (facets?.cms ?? [])
    .filter((c) => c.count > 0)
    .slice(0, 12)
    .map((c) => ({
      value: c.id,
      label: c.id === "none" ? t("scanner.table.noPlatform") : c.id,
      count: c.count,
    }));

  const active = hasActiveFilters(filters);
  const chips = scanFilterChips(filters, t);

  return (
    <div className="space-y-2.5">
      {/* A plain row rather than `Toolbar`: this section sets its own rhythm
          with `space-y`, and `crm-tools` carries a bottom margin that would
          stack on top of it. */}
      <div className="flex flex-wrap items-center gap-2.5">
        <SearchField value={qInput} onChange={setQInput} placeholder={t("scanner.table.searchPlaceholder")} />

        <span className="crm-mut flex items-center gap-1.5 text-[12.5px]">
          <Filter className="size-3.5" />
          {t("scanner.table.count", { count: total })}
        </span>

        <FilterMenu
          label={t("scanner.table.group.score")}
          items={bandItems}
          selected={filters.band ?? []}
          onToggle={toggleBand}
          onClear={() => patch({ band: [] })}
          clearLabel={t("common.clear")}
        />
        <FilterMenu
          label={t("scanner.table.group.kind")}
          items={kindItems}
          selected={filters.kind ?? []}
          onToggle={toggleKind}
          onClear={() => patch({ kind: [] })}
          clearLabel={t("common.clear")}
        />
        <FilterMenu
          label={t("scanner.table.group.platform")}
          items={platformItems}
          selected={filters.cms}
          onToggle={toggleCms}
          onClear={() => patch({ cms: [] })}
          clearLabel={t("common.clear")}
          emptyLabel={t("scanner.table.group.noPlatforms")}
        />
        <FilterMenu
          label={t("scanner.table.group.site")}
          items={flagItems(SITE_FLAGS)}
          selected={selectedFlags(filters, SITE_FLAGS)}
          onToggle={toggleFlag}
          onClear={() => patch(clearFlags(SITE_FLAGS))}
          clearLabel={t("common.clear")}
        />
        <FilterMenu
          label={t("scanner.table.group.contact")}
          items={flagItems(CONTACT_FLAGS)}
          selected={selectedFlags(filters, CONTACT_FLAGS)}
          onToggle={toggleFlag}
          onClear={() => patch(clearFlags(CONTACT_FLAGS))}
          clearLabel={t("common.clear")}
        />

        {actions ? <span className="crm-push flex gap-2.5">{actions}</span> : null}
      </div>

      {active ? (
        <div className="flex flex-wrap items-center gap-1.5 px-1.5">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => patch(chip.patch)}
              className="crm-chip hover:text-red-600"
              title={t("scanner.table.resetFilters")}
            >
              {chip.label}
              <X className="size-3" />
            </button>
          ))}
          <Btn small onClick={reset}>
            <X className="size-3.5" /> {t("scanner.table.resetFilters")}
          </Btn>
        </div>
      ) : null}
    </div>
  );
}
